import { useMemo, useState } from 'react';

import { userApi } from '@/api/services/userApi';
import { Button, Chip, EmptyState } from '@/components/ui';
import type { ChinaAppSysUser } from '@/types/entities';

import { AdminUserForm } from './forms/AdminUserForm';
import { useAdminList } from './lists/adminList';
import { MasterDetail, type MasterItem } from './MasterDetail';

/** Port von `IwmAdminBenutzer.razor` in der Gestalt aus REQ-25 §4.3 (REQ-28 §2). */
const load = () => userApi.get();

/** Ohne Name trägt die E-Mail die Beschriftung; sonst stünde die Zeile leer da. */
function toItem(user: ChinaAppSysUser): MasterItem {
  const name = user.Name ?? '';
  const mail = user.EMail ?? '';

  return {
    id: user.Id,
    label: name === '' ? mail : name,
    description: name === '' ? undefined : mail,
    searchText: `${name} ${mail}`,
    meta: user.HasAnyGroups ? undefined : <Chip text="Kein Zugang" cssClass="e-warning" />,
  };
}

export function AdminUsers() {
  const { rows, loading, error, reload } = useAdminList(load);
  // `null` heißt „nichts gewählt", `0` steht für einen neuen Benutzer.
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const items = useMemo(() => rows.map(toItem), [rows]);

  // Nach dem Speichern zeigt die Liste den gespeicherten Datensatz und bleibt auf ihm; nach
  // dem Löschen meldet `useAdminForm` die 0 und die Auswahl fällt weg (REQ-28 §1).
  const saved = async (id: number) => {
    setSelectedId(id > 0 ? id : null);
    await reload();
  };

  return (
    <MasterDetail
      listLabel="Benutzer"
      items={items}
      selectedId={selectedId}
      onSelect={setSelectedId}
      loading={loading}
      error={error}
      onRetry={() => {
        void reload();
      }}
      searchPlaceholder="Name oder E-Mail"
      emptyText="Keine Benutzer"
      wide
      toolbar={
        <Button
          content="New"
          iconClass="fa-solid fa-plus"
          onClick={() => {
            setSelectedId(0);
          }}
        />
      }
    >
      {selectedId === null ? (
        <EmptyState
          iconClass="fa-solid fa-users"
          title="Kein Benutzer gewählt"
          description="Wählen Sie links einen Benutzer aus oder legen Sie einen neuen an."
        />
      ) : (
        <AdminUserForm id={selectedId} onSave={saved} />
      )}
    </MasterDetail>
  );
}

export default AdminUsers;
